import React, {
  createContext, useContext, useState,
  useEffect, useRef, useCallback,
} from "react";
import { authApi } from "../services/api";

const AuthContext = createContext(null);

function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

const INACTIVITY_MS = 30 * 60 * 1000;
const ACTIVITY_EVENTS = ["mousemove", "keydown", "mousedown", "touchstart", "scroll"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("admin_user"));
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  const tokenTimerRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const silentRefreshRef = useRef(null);
  const logoutRef = useRef(null);
  const resetInactivityRef = useRef(null);
  const scheduleTokenExpiryRef = useRef(null);
  const stableActivityHandler = useRef(() => resetInactivityRef.current?.());

  const logout = useCallback(async (isExpired = false) => {
    if (tokenTimerRef.current) {
      clearTimeout(tokenTimerRef.current);
      tokenTimerRef.current = null;
    }
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }

    ACTIVITY_EVENTS.forEach((ev) =>
      window.removeEventListener(ev, stableActivityHandler.current)
    );

    try {
      if (!isExpired) await authApi.logout();
    } catch {}

    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_refresh_token");
    localStorage.removeItem("admin_user");
    sessionStorage.removeItem("admin_session_only");
    setUser(null);

    setTimeout(() => {
      window.location.href = isExpired ? "/login?expired=1" : "/login";
    }, 100);
  }, []);

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  const scheduleTokenExpiry = useCallback((token) => {
    if (tokenTimerRef.current) {
      clearTimeout(tokenTimerRef.current);
      tokenTimerRef.current = null;
    }
    if (!token) return;

    const expiry = getTokenExpiry(token);
    if (!expiry) return;

    const ms = expiry - Date.now();
    if (ms <= 0) {
      silentRefreshRef.current?.();
      return;
    }

    const fireAt = Math.max(ms - 10000, 0);
    tokenTimerRef.current = setTimeout(() => silentRefreshRef.current?.(), fireAt);
  }, []);

  useEffect(() => {
    scheduleTokenExpiryRef.current = scheduleTokenExpiry;
  }, [scheduleTokenExpiry]);

  const _silentRefresh = useCallback(async () => {
    const refreshToken = localStorage.getItem("admin_refresh_token");
    if (!refreshToken) {
      logoutRef.current?.(true);
      return;
    }

    try {
      const res = await authApi.refresh(refreshToken);
      const data = res.data.data;

      localStorage.setItem("admin_token", data.token);
      localStorage.setItem("admin_refresh_token", data.refreshToken);

      scheduleTokenExpiryRef.current?.(data.token);

      const storedUser = localStorage.getItem("admin_user");
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch {}
      }
    } catch {
      logoutRef.current?.(true);
    }
  }, []);

  silentRefreshRef.current = _silentRefresh;

  useEffect(() => {
    silentRefreshRef.current = _silentRefresh;
  }, [_silentRefresh]);

  const _resetInactivity = useCallback(() => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      logoutRef.current?.(true);
    }, INACTIVITY_MS);
  }, []);

  useEffect(() => {
    resetInactivityRef.current = _resetInactivity;
  }, [_resetInactivity]);

  const startInactivityTimer = useCallback(() => {
    ACTIVITY_EVENTS.forEach((ev) =>
      window.removeEventListener(ev, stableActivityHandler.current)
    );
    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, stableActivityHandler.current, { passive: true })
    );
    resetInactivityRef.current?.();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    const storedUser = localStorage.getItem("admin_user");

    if (token && storedUser) {
      const expiry = getTokenExpiry(token);

      if (expiry && Date.now() >= expiry) {
        Promise.resolve(silentRefreshRef.current?.()).finally(() => setInitializing(false));
      } else {
        try {
          setUser(JSON.parse(storedUser));
        } catch {}
        scheduleTokenExpiry(token);
        startInactivityTimer();
        setInitializing(false);
      }
    } else {
      setInitializing(false);
    }

    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const t = localStorage.getItem("admin_token");
      if (!t) return;
      const expiry = getTokenExpiry(t);
      if (expiry && Date.now() >= expiry) {
        silentRefreshRef.current?.();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (tokenTimerRef.current) clearTimeout(tokenTimerRef.current);
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      ACTIVITY_EVENTS.forEach((ev) =>
        window.removeEventListener(ev, stableActivityHandler.current)
      );
    };
  }, [scheduleTokenExpiry, startInactivityTimer]);

  const finalizeLogin = useCallback((token, refreshToken, userObj, rememberMe = false) => {
    localStorage.setItem("admin_token", token);
    localStorage.setItem("admin_refresh_token", refreshToken);
    localStorage.setItem("admin_user", JSON.stringify(userObj));

    if (rememberMe) {
      sessionStorage.removeItem("admin_session_only");
    } else {
      sessionStorage.setItem("admin_session_only", "true");
    }

    setUser(userObj);
    scheduleTokenExpiry(token);
    startInactivityTimer();
  }, [scheduleTokenExpiry, startInactivityTimer]);

  const login = async (email, password, otpCode, rememberMe = false) => {
    const payload = { email, password };
    if (otpCode) payload.otpCode = otpCode;

    const res = await authApi.login(payload);
    const data = res.data.data;

    if (data.requires2FA || data.requires2FASetup) {
      return data;
    }

    finalizeLogin(
      data.token,
      data.refreshToken,
      { email: data.email, role: data.role },
      rememberMe
    );

    return data;
  };

  const isSuper = user?.role === "ROLE_SUPER_ADMIN";
  const isFinance = user?.role === "ROLE_FINANCE";

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      initializing,
      login,
      logout,
      finalizeLogin,
      isSuper,
      isFinance,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
